// src/components/Timeline.jsx
import { format } from "date-fns";
import React, { useMemo } from "react";
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import "./Timeline.css";
import { useNavigate } from "react-router";
import checkedIcon from "../../../assets/images/icons/checked.svg";
import { taskStatus } from "../../../enums/statuses";

const formatTimelineDate = (date) =>
  date ? format(new Date(date), "MMMM d, yyyy, hh:mma") : "";

const Timeline = ({
  data,
  currentIncident,
  linkedIncidents,
  backToDetailsPage,
}) => {
  const navigate = useNavigate();

  const combinedIncidents = useMemo(
    () => [
      { ...currentIncident, type: "current" },
      ...(data?.map((d) => ({ ...d, type: "related" })) ?? []),
      ...(linkedIncidents?.map((d) => ({ ...d, type: "linked" })) ?? []),
    ],
    [currentIncident, data, linkedIncidents]
  );

  const renderTaskCounts = (d) => {
    if (d.type === "current") {
      const count = d?.tasks?.length ?? 0;
      if (count > 0) {
        const outstandingCount =
          d?.tasks?.filter((task) => task?.status?.id === taskStatus.INPROGRESS)
            ?.length ?? 0;

        return outstandingCount;
      }

      return count;
    } else if (d.type === "related") {
      return d?.onGoingTasksCount + d?.onGoingReviewsCount;
    }
    return null;
  };

  const renderIncidentLabel = (d) => {
    let labelClass = "";
    if (d.type === "current") labelClass = "current-incident-label";
    else if (d.type === "related") labelClass = "related-incident-label";
    else if (d.type === "linked") labelClass = "linked-incident-label";

    return (
      <div className="d-flex align-items-center mb-2">
        <span className={labelClass}>
          {d.type.charAt(0).toUpperCase() + d.type.slice(1)}
        </span>
        <span className="m-0 incident-label-text">{`#${d?.id} : ${d?.name}`}</span>
      </div>
    );
  };

  const contentStyle = (d) => ({
    cursor: "pointer",
    boxShadow: "unset",
    backgroundColor: d.type === "current" ? "#F5F5F5" : "transparent",
  });

  const iconStyle = (d) => ({
    background:
      d.type === "related"
        ? "#eb5b00"
        : d.type === "linked"
        ? "#f3ef17"
        : "#106f60",
    color: "#fff",
    width: "20px",
    height: "20px",
    top: "15px",
    left: "10px",
    boxShadow:
      "0 0 0 4px #fff,inset 0 2px 0 rgba(0,0,0,0.08),0 0px 0 4px rgba(0,0,0,.05)",
  });

  return (
    <div id="timeline">
      <VerticalTimeline layout="1-column-left" lineColor="#71acff">
        {combinedIncidents.length > 0 &&
          combinedIncidents
            .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
            .map((d, index, arr) => (
              <VerticalTimelineElement
                key={`${index}-${d.guid}`}
                onTimelineElementClick={() => {
                  navigate(`/incidents/view/${d.guid}`);
                  backToDetailsPage();
                }}
                className="vertical-timeline-element--work border-0"
                contentStyle={contentStyle(d)}
                dateClassName="timeline-date"
                date={formatTimelineDate(d.createdOn)}
                iconStyle={iconStyle(d)}
                icon={
                  d.type === "current" && (
                    <div className="d-flex justify-content-center align-items-center pt-1">
                      <img
                        width={16}
                        height={16}
                        src={checkedIcon}
                        alt="checked"
                      />
                    </div>
                  )
                }
              >
                {index === arr.length - 1 && <h5>Incident Creation</h5>}
                <h6 className="vertical-timeline-element-title d-flex flex-column align-items-start">
                  {renderIncidentLabel(d)}
                  {(d.type === "current" || d.type === "related") && (
                    <strong>
                      Tasks and reviews outstanding: &nbsp;
                      {renderTaskCounts(d)}
                    </strong>
                  )}
                </h6>
              </VerticalTimelineElement>
            ))}
      </VerticalTimeline>
    </div>
  );
};

export default Timeline;
